import {faFileLines} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Form} from 'antd';
import {useCallback} from 'react';

import {appRoute} from '../../../../../../../app-route';
import {useActionRequiresLicense} from '../../../../../../../provider/license/license-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useModal} from '../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../provider/snackbar/snackbar-hook';
import {useCreatePostMutation} from '../../../../../../../service/posts/posts-hooks';
import {PostFormFieldsEnum, PostFormType, PostStatusEnum} from '../../../../../../../service/posts/posts-types';
import {FeaturesEnum} from '../../../../../../../service/user/user-type';
import {FormInstance} from '../../../../../../../typings/antd';
import {useUrl} from '../../../../../../../util/url-hook/url-hook';

import * as styles from './post-submit-actions.scss';

type PropsType = {
    formInstance: FormInstance<PostFormType>;
    onCreateDraft: () => void;
    handleSubmitError: (error: unknown) => void;
    isDisabled: boolean;
};

export function NewPostActions(props: PropsType): JSX.Element {
    const {formInstance, onCreateDraft, handleSubmitError, isDisabled} = props;

    const createDateTime = Form.useWatch(PostFormFieldsEnum.CreateDateTime, formInstance);

    const {mutateAsync: sendPost, isLoading: isCreating} = useCreatePostMutation(
        createDateTime ? PostStatusEnum.Planned : PostStatusEnum.Sent
    );
    const {mutateAsync: createDraft, isLoading: isCreatingDraft} = useCreatePostMutation(PostStatusEnum.Draft);

    const {snackbar} = useSnackbar();
    const {modal} = useModal();
    const {pushUrl} = useUrl();
    const {postsManagement} = appRoute;

    const handlePostSend = useActionRequiresLicense(
        useCallback(async () => {
            await formInstance.validateFields();

            modal.confirm({
                cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
                okText: <Locale stringKey="POPUP__BUTTON__CONFIRM" />,
                title: <Locale stringKey="POSTS_SUBMIT_INFORMATION__HEADING" />,
                content: <Locale stringKey="POSTS_FORM__MESSAGE__CREATE_POST_MODAL__CONTENT" />,
                onOk: async () => {
                    try {
                        const formValue = formInstance.getFieldValue([]) as PostFormType;

                        await sendPost(formValue);

                        snackbar.success(<Locale stringKey="POSTS_FORM__MESSAGE__CREATE_SUCCESS" />);

                        pushUrl(postsManagement.path);
                    } catch (error) {
                        handleSubmitError(error);

                        const errorMessage = (error as {jsonData?: {posts?: Array<{text?: Array<string>}>}})?.jsonData
                            ?.posts?.[0]?.text?.[0];

                        if (errorMessage) {
                            snackbar.error(errorMessage);
                        } else {
                            snackbar.error(<Locale stringKey="POSTS_FORM__ERROR__CREATE_POST" />);
                        }
                    }
                },
            });
        }, [modal, formInstance, sendPost, snackbar, pushUrl, postsManagement.path, handleSubmitError]),
        FeaturesEnum.posts
    );

    const handleSaveDraft = useActionRequiresLicense(
        useCallback(async () => {
            await formInstance.validateFields();

            try {
                const formValue = formInstance.getFieldValue([]) as PostFormType;
                const idNumberType = await createDraft(formValue);

                formInstance.setFieldValue([PostFormFieldsEnum.Id], idNumberType.id);
                onCreateDraft();
                snackbar.success(<Locale stringKey="POSTS_FORM__MESSAGE__SAVE_DRAFT_SUCCESS" />);
            } catch (error: unknown) {
                handleSubmitError(error);

                const errorMessage = (error as {jsonData?: {posts?: Array<{text?: Array<string>}>}})?.jsonData
                    ?.posts?.[0]?.text?.[0];

                if (errorMessage) {
                    snackbar.error(errorMessage);
                } else {
                    snackbar.error(<Locale stringKey="POSTS_FORM__ERROR__CREATE_POST" />);
                }
            }
        }, [onCreateDraft, createDraft, formInstance, handleSubmitError, snackbar]),
        FeaturesEnum.posts
    );

    const isInProgress = isCreating || isCreatingDraft;

    return (
        <div className={styles.PostSubmitButtons}>
            <Button
                className={styles.PostSubmitButtons_button}
                disabled={isInProgress || isDisabled}
                loading={isCreating}
                onClick={handlePostSend}
                type="primary"
            >
                <Locale stringKey="POSTS_FORM__BUTTON__SEND" />
            </Button>

            <Button
                className={styles.PostSubmitButtons_button}
                disabled={isInProgress || isDisabled}
                icon={<FontAwesomeIcon className={styles.PostSubmitButtons_buttonIcon} icon={faFileLines} />}
                loading={isCreatingDraft}
                onClick={handleSaveDraft}
            >
                <Locale stringKey="POSTS_FORM__BUTTON__SAVE_DRAFT" />
            </Button>
        </div>
    );
}
